import { ChevronLeft, ChevronRight } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useAppSelector } from '@/store'
import { Icon } from '@/common/components/Icon'

export interface PaginationProps {
  page: number
  size: number
  setSize: (size: number) => void
  totalElements: number
  changePage: (page: number) => void
  className?: string
  nested?: boolean
}

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function getPageItems(page: number, totalPages: number) {
  // 1 … (p-2) (p-1) p (p+1) (p+2) … total
  const current = clamp(page, 1, totalPages)
  const pages = new Set<number>([1, totalPages])
  for (let p = current - 2; p <= current + 2; p += 1) {
    if (p >= 1 && p <= totalPages) pages.add(p)
  }

  const sorted = Array.from(pages).sort((a, b) => a - b)
  const items: Array<number | 'ellipsis'> = []
  let prev: number | null = null
  for (const p of sorted) {
    if (prev !== null && p - prev > 1) items.push('ellipsis')
    items.push(p)
    prev = p
  }
  return items
}

export const Pagination = (props: PaginationProps): JSX.Element => {
  const { page, size, setSize, totalElements, changePage, className, nested } =
    props

  const totalPages = Math.max(1, Math.ceil(totalElements / size))
  const currentPage = clamp(page, 1, totalPages)
  const pageItems = getPageItems(currentPage, totalPages)
  const { isSidebarCollapsed } = useAppSelector((s) => s.app)

  const content = (
    <div className="flex flex-nowrap items-center justify-center gap-4 py-3">
      <div className="text-subtitle-m text-monochrome-1 line-clamp-1">
        Total {totalElements} items
      </div>

      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          className="w-[50px] h-[36px] border border-transparent rounded-[10px] !bg-monochrome-5-50 text-monochrome-1 hover:bg-transparent hover:text-monochrome-1"
          disabled={currentPage <= 1}
          onClick={() => changePage(currentPage - 1)}
          aria-label="Previous page"
        >
          <Icon
            icon="chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          />
        </Button>

        {pageItems.map((item, idx) => {
          if (item === 'ellipsis') {
            return (
              <Button
                type="button"
                size="icon-sm"
                variant="ghost"
                className="w-[50px] h-[36px] border border-transparent rounded-[10px] !bg-monochrome-5-50 text-monochrome-1"
                key={`ellipsis-${idx}`}
              >
                …
              </Button>
            )
          }

          const p = item
          const isActive = p === currentPage
          return (
            <Button
              key={`page-${p}`}
              type="button"
              variant="ghost"
              size="sm"
              className={cn(
                'w-[50px] h-[36px] border border-transparent rounded-[10px] px-3 !bg-monochrome-5-50 text-monochrome-1',
                !isActive
                  ? '!bg-monochrome-5-50 text-monochrome-1 hover:bg-white/5'
                  : 'bg-gradient-6 border-monochrome-1',
              )}
              onClick={() => changePage(p)}
              aria-current={isActive ? 'page' : undefined}
            >
              {p}
            </Button>
          )
        })}

        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="w-[50px] h-[36px] border border-transparent rounded-[10px] !bg-monochrome-5-50 text-monochrome-1"
          disabled={currentPage >= totalPages}
          onClick={() => changePage(currentPage + 1)}
          aria-label="Next page"
        >
          <Icon
            icon="chevron-left"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            className="rotate-180"
          />
        </Button>
      </div>

      <div className="w-[150px]">
        <Select
          value={String(size)}
          onValueChange={(value) => setSize(Number(value) || 1)}
        >
          <SelectTrigger className="h-10 rounded-[10px] bg-monochrome-5-50 text-monochrome-1 border border-monochrome-120">
            <SelectValue placeholder={`${size} / page`} />
          </SelectTrigger>
          <SelectContent
            side="top"
            className="bg-monochrome-120 border-monochrome-120 backdrop-blur-xl"
          >
            {[10, 20, 30, 40, 50, 100].map((opt) => (
              <SelectItem key={opt} value={String(opt)}>
                {opt} / page
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2 text-subtitle-m text-monochrome-1">
        Go to
        <Input
          className="h-10 w-[70px] rounded-[10px] !bg-monochrome-5-50 text-monochrome-1 border border-monochrome-120"
          type="number"
          min={1}
          placeholder="f.e. 50"
          max={totalPages}
          onKeyUp={(value) => {
            if (value.currentTarget.value !== '') {
              const min = parseInt(value.currentTarget.min)
              const max = parseInt(value.currentTarget.max)
              const v = parseInt(value.currentTarget.value)
              if (v < min) value.currentTarget.value = String(min)
              if (v > max) value.currentTarget.value = String(max)
            }
          }}
          onChange={(e) => {
            if (e.target.value !== '') {
              const v = parseInt(e.target.value)
              changePage(clamp(v, 1, totalPages))
            }
          }}
        />
      </div>
    </div>
  )

  return (
    <>
      {/* Spacer so fixed pagination doesn't cover content */}
      <div className="h-[108px]" aria-hidden />

      <div
        className={cn(
          'fixed inset-x-0 bottom-0 z-40 px-4 pb-[calc(env(safe-area-inset-bottom)+12px)] transition-all duration-300',
          className,
          isSidebarCollapsed
            ? 'px-5 ps-5'
            : nested
            ? 'px-5 ps-5'
            : 'ps-[calc(375px+40px)]',
        )}
      >
        <div className="mx-auto w-full rounded-[32px]">{content}</div>
      </div>
    </>
  )
}
